import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Sparkles, CalendarCheck, CalendarDays, MapPin, Plus, X, Loader2,
  Trash2, Clock, DollarSign, FolderOpen, ChevronLeft, ChevronRight, Ban, Save,
  Video, Eye, Search, Check, Pencil, Users, ImagePlus, Share2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  myservicesOverview, addService, toggleService, deleteService, shareService, setServiceImage, uploadFile,
  getAvailability, saveAvailability, getDaySlots, blockTimes,
  listMyEvents, saveEvent, toggleEvent, deleteEvent, getApprovedLocations,
} from "@/lib/api";
import { toast } from "sonner";

const fmtMoney = (p) => (p === "" || p == null ? "" : `$${Number(p).toFixed(2)}`);

function ServiceImagePicker({ url, onChange, rec, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const r = await uploadFile(f, rec, "gallery"); onChange(r.url); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        className="h-20 w-20 shrink-0 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 overflow-hidden flex items-center justify-center text-zinc-400 hover:border-indigo-400 hover:text-indigo-500 disabled:opacity-50" data-testid={`${testId}-btn`}>
        {busy ? <Loader2 size={18} className="animate-spin" /> : url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <ImagePlus size={20} />}
      </button>
      <div className="text-xs text-zinc-500">
        <div className="font-semibold text-zinc-700">{url ? "Service image" : "Add an image"}</div>
        <div>{url ? "Tap to replace" : "Shown on your services and booking page"}</div>
        {url && <button type="button" onClick={() => onChange("")} className="text-rose-600 hover:underline mt-0.5" data-testid={`${testId}-remove`}>Remove</button>}
      </div>
    </div>
  );
}

function AddServiceModal({ library, onClose, onCreate, onPickLibrary, busy, rec, isAdmin }) {
  const [tab, setTab] = useState("new");
  const [form, setForm] = useState({ name: "", description: "", duration: "", price: "", image_url: "", shared: false });
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="add-service-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg max-h-[90vh] bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h3 className="text-lg font-bold">Add New Service</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-zinc-100 flex items-center justify-center" data-testid="add-service-close"><X size={18} /></button>
        </div>
        <div className="flex border-b border-zinc-100">
          {[["new", "Create New"], ["library", "From Library"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex-1 py-3 text-sm font-semibold transition-colors ${tab === k ? "text-indigo-600 border-b-2 border-indigo-600" : "text-zinc-500"}`} data-testid={`add-tab-${k}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "new" ? (
          <div className="p-5 space-y-4 overflow-y-auto">
            <ServiceImagePicker url={form.image_url} onChange={(u) => setForm({ ...form, image_url: u })} rec={rec} testId="svc-image" />
            <div>
              <label className="block text-sm font-bold mb-1">Service Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Swedish Massage" className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm" data-testid="svc-name" />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the service…" rows={3} className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm" data-testid="svc-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold mb-1">Duration</label>
                <input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="Minutes (e.g., 60)" inputMode="numeric" className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm" data-testid="svc-duration" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Price</label>
                <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="e.g., 99" inputMode="decimal" className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm" data-testid="svc-price" />
              </div>
            </div>
            {isAdmin && (
              <label className="flex items-center gap-2 cursor-pointer" data-testid="svc-shared-wrap">
                <input type="checkbox" checked={form.shared} onChange={(e) => setForm({ ...form, shared: e.target.checked })} className="accent-indigo-600" data-testid="svc-shared" />
                <span className="text-sm font-semibold text-zinc-700">Share to every dealer's library</span>
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 h-11 rounded-xl border border-zinc-200 text-sm font-semibold">Cancel</button>
              <button onClick={() => onCreate(form)} disabled={busy || !form.name.trim()} className="px-5 h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-bold" data-testid="svc-create-btn">
                {busy ? "Creating…" : "Create Service"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-2.5 overflow-y-auto" data-testid="library-list">
            {library.length === 0 ? (
              <p className="text-sm text-zinc-400 text-center py-8">No shared services yet.</p>
            ) : (
              library.map((s, i) => (
                <button key={i} onClick={() => onPickLibrary(s)} disabled={busy || s.already_added} className="w-full text-left rounded-xl border border-zinc-200 hover:border-indigo-300 hover:bg-indigo-50/40 p-3 disabled:opacity-60 flex gap-3" data-testid={`library-item-${i}`}>
                  <div className="h-16 w-16 shrink-0 rounded-lg bg-zinc-100 overflow-hidden flex items-center justify-center text-zinc-300">
                    {s.image_url ? <img src={s.image_url} alt="" className="h-full w-full object-cover" data-testid={`library-img-${i}`} /> : <Sparkles size={18} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-zinc-900 flex items-center gap-2">{s.name}
                      {s.already_added && <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><Check size={10} /> Added</span>}
                    </div>
                    {s.category && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-md px-2 py-0.5 mt-1">
                        <FolderOpen size={12} /> {s.category}
                      </span>
                    )}
                    {s.description && <div className="text-sm text-zinc-500 mt-1 line-clamp-2">{s.description}</div>}
                    <div className="text-xs text-zinc-400 mt-1">{[s.duration && `${s.duration} min`, s.price && fmtMoney(s.price)].filter(Boolean).join(" · ")}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ServicesTab({ rec }) {
  const [data, setData] = useState({ address: {}, services: [], library: [] });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    myservicesOverview(rec).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [rec]);
  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    setBusy(true);
    try {
      await addService({ ...form, caller_user_record_id: rec });
      toast.success("Service added");
      setShowAdd(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    finally { setBusy(false); }
  };
  const pickLibrary = async (s) => {
    setBusy(true);
    try {
      const r = await addService({ name: s.name, description: s.description, duration: s.duration, price: s.price, category: s.category, image_url: s.image_url, caller_user_record_id: rec });
      toast.success(r?.duplicate ? `"${s.name}" is already in your services` : `Added "${s.name}"`);
      setShowAdd(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    finally { setBusy(false); }
  };
  const toggle = async (svc) => {
    setData((d) => ({ ...d, services: d.services.map((x) => x.id === svc.id ? { ...x, active: !x.active } : x) }));
    try { await toggleService(svc.id, !svc.active, rec); } catch { load(); }
  };
  const share = async (svc) => {
    setData((d) => ({ ...d, services: d.services.map((x) => x.id === svc.id ? { ...x, shared: !x.shared } : x) }));
    try { await shareService(svc.id, !svc.shared, rec); toast.success(!svc.shared ? "Shared to all dealers' library" : "Removed from library"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); load(); }
  };
  const setImage = async (svc, url) => {
    setData((d) => ({ ...d, services: d.services.map((x) => x.id === svc.id ? { ...x, image_url: url } : x) }));
    try { await setServiceImage(svc.id, url, rec); toast.success(url ? "Image saved" : "Image removed"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); load(); }
  };
  const remove = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"?`)) return;
    setData((d) => ({ ...d, services: d.services.filter((x) => x.id !== svc.id) }));
    try { await deleteService(svc.id, rec); toast.success("Deleted"); } catch { load(); }
  };

  const a = data.address || {};
  return (
    <div>
      <div className="rounded-2xl bg-white border border-zinc-200 shadow-sm p-5 mb-5 flex items-start justify-between gap-3" data-testid="myservices-address">
        <div>
          <div className="font-bold text-zinc-900">{a.company || "Your Spa"}</div>
          <div className="text-sm text-zinc-500 mt-1 inline-flex items-center gap-1.5">
            <MapPin size={14} /> {[a.address, a.city, a.state, a.postcode].filter(Boolean).join(", ") || "Add your address in Profile"}
          </div>
        </div>
        <button onClick={() => setShowAdd(true)} className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold" data-testid="add-service-btn">
          <Plus size={16} /> Add Service
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-zinc-400"><Loader2 className="animate-spin inline" size={20} /></div>
      ) : data.services.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white py-16 text-center" data-testid="services-empty">
          <Sparkles size={36} className="mx-auto text-zinc-300" />
          <p className="mt-3 font-bold text-zinc-700">No services yet</p>
          <p className="text-sm text-zinc-500 mt-1">Add your first service or pick from the library.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="services-list">
          {data.services.map((s) => (
            <div key={s.id} className="rounded-2xl border border-zinc-200 bg-white shadow-sm p-4 flex gap-3" data-testid={`service-card-${s.id}`}>
              <ServiceCardImage svc={s} rec={rec} onChange={(u) => setImage(s, u)} />
              <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-zinc-900">{s.name}</div>
                <button onClick={() => remove(s)} className="text-zinc-300 hover:text-rose-600" data-testid={`service-delete-${s.id}`}><Trash2 size={16} /></button>
              </div>
              {s.description && <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{s.description}</p>}
              <div className="flex items-center gap-3 text-xs text-zinc-500 mt-2">
                {s.duration && <span className="inline-flex items-center gap-1"><Clock size={12} /> {s.duration} min</span>}
                {s.price !== "" && <span className="inline-flex items-center gap-1"><DollarSign size={12} /> {fmtMoney(s.price)}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={s.active} onChange={() => toggle(s)} className="accent-indigo-600" data-testid={`service-toggle-${s.id}`} />
                  <span className="text-xs font-semibold text-zinc-600">{s.active ? "Active" : "Hidden"}</span>
                </label>
                {data.is_admin ? (
                  <label className="flex items-center gap-2 cursor-pointer" title="Show in every dealer's library">
                    <input type="checkbox" checked={!!s.shared} onChange={() => share(s)} className="accent-emerald-600" data-testid={`service-share-${s.id}`} />
                    <span className="text-xs font-semibold text-zinc-600 inline-flex items-center gap-1"><Share2 size={11} /> {s.shared ? "Shared" : "Share"}</span>
                  </label>
                ) : s.shared ? <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">Shared</span> : null}
              </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddServiceModal library={data.library} onClose={() => setShowAdd(false)} onCreate={create} onPickLibrary={pickLibrary} busy={busy} rec={rec} isAdmin={!!data.is_admin} />
      )}
    </div>
  );
}

function ServiceCardImage({ svc, rec, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const r = await uploadFile(f, rec, "gallery"); onChange(r.url); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="shrink-0">
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`service-image-file-${svc.id}`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} title={svc.image_url ? "Replace image" : "Add image"}
        className="h-20 w-20 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 overflow-hidden flex items-center justify-center text-zinc-400 hover:border-indigo-400 hover:text-indigo-500" data-testid={`service-image-${svc.id}`}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : svc.image_url ? <img src={svc.image_url} alt="" className="h-full w-full object-cover" /> : <ImagePlus size={18} />}
      </button>
    </div>
  );
}

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtTime12(hm) {
  const [h, m] = hm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${ap}`;
}

function TimeBlockSection({ rec }) {
  const [date, setDate] = useState(localYmd(new Date()));
  const [slots, setSlots] = useState([]);
  const [blockedSet, setBlockedSet] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    if (!date) return;
    setLoading(true);
    getDaySlots(rec, date)
      .then((d) => {
        setSlots(d.slots || []);
        setBlockedSet(new Set((d.slots || []).filter((s) => s.status === "blocked").map((s) => s.time)));
        setDirty(false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [rec, date]);
  useEffect(() => { load(); }, [load]);

  const toggle = (s) => {
    if (s.status === "booked") return;
    setBlockedSet((prev) => {
      const next = new Set(prev);
      next.has(s.time) ? next.delete(s.time) : next.add(s.time);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await blockTimes(rec, date, [...blockedSet]);
      toast.success("Time blocks saved");
      setDirty(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm p-5 mt-5" data-testid="timeblock-section">
      <h3 className="font-bold text-zinc-900 inline-flex items-center gap-2"><Clock size={16} className="text-indigo-500" /> Block specific times</h3>
      <p className="text-sm text-zinc-500 mt-1 mb-4">Pick a day, then tap any open slot to block it. <span className="text-emerald-700 font-semibold">Reserved</span> slots booked by customers on your page show here too.</p>
      <div className="flex items-center gap-3 mb-4">
        <input type="date" value={date} min={localYmd(new Date())} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm font-semibold" data-testid="timeblock-date" />
        <div className="flex items-center gap-3 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-500 inline-block" /> Reserved</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-500 inline-block" /> Blocked</span>
        </div>
      </div>
      {loading ? (
        <div className="py-10 text-center text-zinc-400"><Loader2 className="animate-spin inline" size={18} /></div>
      ) : slots.length === 0 ? (
        <p className="text-sm text-zinc-400 py-6 text-center">Add a service first to manage time slots.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="timeblock-grid">
          {slots.map((s) => {
            const isBooked = s.status === "booked";
            const isBlocked = blockedSet.has(s.time);
            return (
              <button
                key={s.time}
                disabled={isBooked}
                onClick={() => toggle(s)}
                title={isBooked && s.info ? `${s.info.name} · ${s.info.service}` : ""}
                className={`rounded-lg py-2 px-1 text-xs font-semibold border transition-colors ${
                  isBooked ? "bg-emerald-50 border-emerald-200 text-emerald-700 cursor-not-allowed" :
                  isBlocked ? "bg-rose-500 border-rose-500 text-white" :
                  "border-zinc-200 text-zinc-700 hover:border-rose-300 hover:bg-rose-50"
                }`}
                data-testid={`timeblock-slot-${s.time}`}
              >
                <div>{fmtTime12(s.time)}</div>
                {isBooked && <div className="text-[9px] font-normal truncate mt-0.5">{s.info?.name || "Reserved"}</div>}
              </button>
            );
          })}
        </div>
      )}
      <button onClick={save} disabled={saving || !dirty} className="mt-5 w-full sm:w-auto px-6 h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-bold inline-flex items-center justify-center gap-2" data-testid="timeblock-save-btn">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> Save Time Blocks</>}
      </button>
    </div>
  );
}

function AvailabilityTab({ rec }) {
  const [month, setMonth] = useState(() => new Date());
  const [blocked, setBlocked] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const today = localYmd(new Date());

  useEffect(() => {
    setLoading(true);
    getAvailability(rec)
      .then((d) => setBlocked(new Set(d.blocked_dates || [])))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [rec]);

  const toggle = (key) => {
    if (key < today) return;
    setBlocked((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveAvailability(rec, [...blocked]);
      toast.success("Availability saved");
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const sortedBlocked = [...blocked].sort();

  if (loading) return <div className="py-16 text-center text-zinc-400"><Loader2 className="animate-spin inline" size={20} /></div>;

  return (
    <div data-testid="availability-tab">
    <div className="grid md:grid-cols-[1fr_280px] gap-5">
      <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-zinc-900 inline-flex items-center gap-2"><Ban size={16} className="text-rose-500" /> Block out dates</h3>
        </div>
        <p className="text-sm text-zinc-500 mb-4">Tap any day to block it — customers won't be able to book on blocked days. Tap again to re-open.</p>
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="h-8 w-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500" data-testid="avail-prev"><ChevronLeft size={18} /></button>
          <span className="font-bold text-zinc-800">{month.toLocaleString("en-US", { month: "long", year: "numeric" })}</span>
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="h-8 w-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500" data-testid="avail-next"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center mb-1">
          {DOW.map((d) => <div key={d} className="text-[11px] font-semibold text-zinc-400 py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="h-11" />;
            const key = localYmd(new Date(month.getFullYear(), month.getMonth(), d));
            const isPast = key < today;
            const isBlocked = blocked.has(key);
            return (
              <button
                key={i}
                disabled={isPast}
                onClick={() => toggle(key)}
                className={`h-11 rounded-lg text-sm font-semibold transition-colors ${
                  isBlocked ? "bg-rose-500 text-white line-through" :
                  isPast ? "text-zinc-300 cursor-not-allowed" :
                  "text-zinc-700 hover:bg-rose-50"
                }`}
                data-testid={`avail-day-${key}`}
              >
                {d}
              </button>
            );
          })}
        </div>
        <button onClick={save} disabled={saving || !dirty} className="mt-5 w-full h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-bold inline-flex items-center justify-center gap-2" data-testid="avail-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> Save Availability</>}
        </button>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm p-5">
        <h4 className="font-bold text-zinc-900 mb-3">Blocked days ({sortedBlocked.length})</h4>
        {sortedBlocked.length === 0 ? (
          <p className="text-sm text-zinc-400">No blocked days. You're open for bookings.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="blocked-chips">
            {sortedBlocked.map((k) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-rose-50 text-rose-700 rounded-full pl-3 pr-1.5 py-1" data-testid={`blocked-chip-${k}`}>
                {new Date(k + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                <button onClick={() => toggle(k)} className="h-4 w-4 rounded-full hover:bg-rose-200 flex items-center justify-center"><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
    <TimeBlockSection rec={rec} />
    </div>
  );
}

const fmtEventDate = (iso) => {
  if (!iso) return "No date";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};
const isoToLocalInput = (iso) => (iso ? String(iso).slice(0, 16) : "");

function LocationPicker({ users, valueId, valueText, onUser, onText }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => users.find((u) => u.id === valueId), [users, valueId]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users.slice(0, 50);
    return users.filter((u) => `${u.label} ${u.location}`.toLowerCase().includes(s)).slice(0, 50);
  }, [users, q]);
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-3.5 text-zinc-400" />
        <input
          value={open ? q : (selected ? selected.label : q)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search an approved dealer/spa…"
          className="w-full h-11 rounded-xl border border-zinc-200 pl-9 pr-3 text-sm"
          data-testid="event-loc-search"
        />
      </div>
      {open && (
        <div className="max-h-44 overflow-y-auto rounded-xl border border-zinc-200 divide-y divide-zinc-100" data-testid="event-loc-list">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-zinc-400">No matches.</div>
          ) : filtered.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => { onUser(u); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 hover:bg-indigo-50/50 flex items-center justify-between gap-2"
              data-testid={`event-loc-opt-${u.id}`}
            >
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-zinc-800 truncate">{u.label}</span>
                {u.location && <span className="block text-xs text-zinc-400 truncate">{u.location}</span>}
              </span>
              {valueId === u.id && <Check size={14} className="text-indigo-600 shrink-0" />}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 text-xs text-zinc-400"><span className="flex-1 border-t border-zinc-100" />or type a custom location<span className="flex-1 border-t border-zinc-100" /></div>
      <input
        value={valueText}
        onChange={(e) => { onText(e.target.value); }}
        placeholder="e.g., Zoom / Hyatt Ballroom, Miami FL"
        className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm"
        data-testid="event-loc-custom"
      />
    </div>
  );
}

function EventModal({ rec, isAdmin, approvedLocations, event, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    id: event?.id || "",
    title: event?.title || "",
    description: event?.description || "",
    date_time: isoToLocalInput(event?.date_time),
    max_guests: event?.max_guests || "",
    publish: event?.public ?? false,
    active: event?.active ?? true,
    zoom_required: event?.zoom_required ?? false,
    zoom_url: event?.zoom_url || "",
    zoom_password: event?.zoom_password || "",
    location: event?.location || "",
    location_user_id: "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const res = await saveEvent({ ...form, caller_user_record_id: rec });
      toast.success(form.id ? "Event updated" : "Event created");
      onSaved(res?.event);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save event");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="event-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg max-h-[92vh] bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h3 className="text-lg font-bold">{form.id ? "Edit Event" : "Add Event"}</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-zinc-100 flex items-center justify-center" data-testid="event-modal-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-bold mb-1">Event Title *</label>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g., Cellcode Wellness Tour" className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm" data-testid="event-title" />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="What's this event about?" className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm" data-testid="event-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold mb-1">Date &amp; Time</label>
              <input type="datetime-local" value={form.date_time} onChange={(e) => set("date_time", e.target.value)} className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm" data-testid="event-datetime" />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Max Guests</label>
              <input value={form.max_guests} onChange={(e) => set("max_guests", e.target.value)} inputMode="numeric" placeholder="e.g., 50" className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm" data-testid="event-maxguests" />
            </div>
          </div>

          {/* Zoom */}
          <div className="rounded-xl border border-zinc-200 p-3">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="inline-flex items-center gap-2 text-sm font-bold text-zinc-800"><Video size={16} className="text-indigo-500" /> Zoom required</span>
              <input type="checkbox" checked={form.zoom_required} onChange={(e) => set("zoom_required", e.target.checked)} className="accent-indigo-600 h-5 w-5" data-testid="event-zoom-required" />
            </label>
            {form.zoom_required && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <input value={form.zoom_url} onChange={(e) => set("zoom_url", e.target.value)} placeholder="Zoom link (https://…)" className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm" data-testid="event-zoom-url" />
                <input value={form.zoom_password} onChange={(e) => set("zoom_password", e.target.value)} inputMode="numeric" placeholder="Zoom passcode" className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm" data-testid="event-zoom-password" />
              </div>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-bold mb-1 inline-flex items-center gap-1.5"><MapPin size={14} className="text-indigo-500" /> Event Location</label>
            {isAdmin ? (
              <LocationPicker
                users={approvedLocations}
                valueId={form.location_user_id}
                valueText={form.location}
                onUser={(u) => setForm((f) => ({ ...f, location_user_id: u.id, location: u.location || u.label }))}
                onText={(t) => setForm((f) => ({ ...f, location: t, location_user_id: "" }))}
              />
            ) : (
              <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Defaults to your spa address" className="w-full h-11 rounded-xl border border-zinc-200 px-3 text-sm" data-testid="event-loc-custom" />
            )}
          </div>

          {/* Toggles */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center justify-between rounded-xl border border-zinc-200 px-3 py-2.5 cursor-pointer">
              <span className="inline-flex items-center gap-2 text-sm font-bold text-zinc-800"><Eye size={16} className="text-emerald-500" /> Publish</span>
              <input type="checkbox" checked={form.publish} onChange={(e) => set("publish", e.target.checked)} className="accent-emerald-600 h-5 w-5" data-testid="event-publish" />
            </label>
            <label className="flex items-center justify-between rounded-xl border border-zinc-200 px-3 py-2.5 cursor-pointer">
              <span className="inline-flex items-center gap-2 text-sm font-bold text-zinc-800"><CalendarCheck size={16} className="text-indigo-500" /> Active</span>
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-indigo-600 h-5 w-5" data-testid="event-active" />
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 h-11 rounded-xl border border-zinc-200 text-sm font-semibold">Cancel</button>
            <button onClick={submit} disabled={busy || !form.title.trim()} className="px-5 h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-bold inline-flex items-center gap-2" data-testid="event-save-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {form.id ? "Save Event" : "Create Event"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventsTab({ rec }) {
  const [events, setEvents] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [approvedLocations, setApprovedLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // event object or {} for new

  const load = useCallback(() => {
    setLoading(true);
    listMyEvents(rec)
      .then((d) => { setEvents(d.events || []); setIsAdmin(!!d.is_admin); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [rec]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin && approvedLocations.length === 0) {
      getApprovedLocations(rec).then(setApprovedLocations).catch(() => {});
    }
  }, [isAdmin, rec, approvedLocations.length]);

  const toggle = async (ev, field) => {
    const key = field === "active" ? "active" : "public";
    const cur = field === "active" ? ev.active : ev.public;
    setEvents((list) => list.map((x) => x.id === ev.id ? { ...x, [field === "active" ? "active" : "public"]: !cur } : x));
    try { await toggleEvent(ev.id, field, !cur, rec); } catch { load(); }
  };
  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    setEvents((list) => list.filter((x) => x.id !== ev.id));
    try { await deleteEvent(ev.id, rec); toast.success("Event deleted"); } catch { load(); }
  };

  return (
    <div data-testid="events-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-zinc-500">{events.length} event{events.length === 1 ? "" : "s"}</p>
        <button onClick={() => setEditing({})} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold" data-testid="add-event-btn">
          <Plus size={16} /> Add Event
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-zinc-400"><Loader2 className="animate-spin inline" size={20} /></div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white py-16 text-center" data-testid="events-empty">
          <CalendarCheck size={36} className="mx-auto text-zinc-300" />
          <p className="mt-3 font-bold text-zinc-700">No events yet</p>
          <p className="text-sm text-zinc-500 mt-1">Create your first event to share with your guests.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="events-list">
          {events.map((ev) => (
            <div key={ev.id} className="rounded-2xl border border-zinc-200 bg-white shadow-sm p-4" data-testid={`event-card-${ev.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-zinc-900 truncate">{ev.title}</div>
                  <div className="text-xs text-zinc-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {fmtEventDate(ev.date_time)}</span>
                    {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {ev.location}</span>}
                    {ev.max_guests > 0 && <span className="inline-flex items-center gap-1"><Users size={12} /> {ev.max_guests}</span>}
                    {ev.zoom_required && <span className="inline-flex items-center gap-1 text-indigo-600"><Video size={12} /> Zoom</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(ev)} className="h-8 w-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500" data-testid={`event-edit-${ev.id}`}><Pencil size={15} /></button>
                  <button onClick={() => remove(ev)} className="h-8 w-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-zinc-300 hover:text-rose-600" data-testid={`event-delete-${ev.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-100">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={!!ev.public} onChange={() => toggle(ev, "public")} className="accent-emerald-600" data-testid={`event-toggle-publish-${ev.id}`} />
                  <span className="text-xs font-semibold text-zinc-600">{ev.public ? "Published" : "Draft"}</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={!!ev.active} onChange={() => toggle(ev, "active")} className="accent-indigo-600" data-testid={`event-toggle-active-${ev.id}`} />
                  <span className="text-xs font-semibold text-zinc-600">{ev.active ? "Active" : "Inactive"}</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EventModal
          rec={rec}
          isAdmin={isAdmin}
          approvedLocations={approvedLocations}
          event={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

export default function MyServices() {
  const { user } = useAuth();
  const rec = user?.user_record_id || user?.record_id || "";
  const [tab, setTab] = useState("services");
  const tabs = [
    { k: "services", label: "Services", icon: Sparkles },
    { k: "events", label: "Events", icon: CalendarCheck },
    { k: "availability", label: "Availability", icon: CalendarDays },
  ];
  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="myservices-page">
      <h1 className="text-2xl font-bold text-zinc-900 mb-1">My Services</h1>
      <p className="text-sm text-zinc-500 mb-5">Manage your spa services, bookings and events.</p>
      <div className="flex gap-1 bg-zinc-100 rounded-xl p-1 mb-6 w-fit" data-testid="myservices-tabs">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t.k ? "bg-white shadow-sm text-indigo-700" : "text-zinc-500"}`} data-testid={`myservices-tab-${t.k}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "services" && <ServicesTab rec={rec} />}
      {tab === "events" && <EventsTab rec={rec} />}
      {tab === "availability" && <AvailabilityTab rec={rec} />}
    </div>
  );
}
